<script setup lang="ts">
import { type IBookBase } from "~/types/book";
import { useDevice } from "#imports";
import useBook from "~/composible/useBook";

interface IProps {
  book: IBookBase
}

const config = useRuntimeConfig()
const { isMobile } = useDevice();
const { getLinkToApp } = useBook();

const props = defineProps<IProps>();

const genre = computed(() => props.book.genres
    ? props.book.genres[0]
    : null
);

</script>

<template>
  <div class="content-book-card flex flex-col p-4 rounded-xl bg-white dark:border-zinc-950 dark:border-[1px] dark:bg-zinc-900 gap-8 max-md:gap-4 max-md:rounded-none max-md:mx-[-15px]">
    <div class="grid grid-cols-[auto_1fr] grid-rows-[auto_1fr] grid-flow-row-dense items-start gap-x-4 gap-y-4 max-md:gap-3">
      <div class="book-details__cover md:row-span-2 flex-none">
        <BookCover
            :path="book.cover_path"
            :url="book.cover"
            :alt="`Book cover of “${book.title}“ by ${book.author}`"
            :quality="80"
            :width="isMobile ? 104 : 124"
            :height="isMobile? 145 : 173"
            format="webp"
            class="max-md:w-[104px]"
        />
      </div>
      <div class="flex flex-col gap-4 book-details__content">
        <div>
          <nuxt-link :to="`/books/${book.slug }`">
            <h2 class="text-2xl font-bold max-md:font-semibold max-md:text-xl mb-2 max-md:mb-3">{{ book?.title }}</h2>
          </nuxt-link>
          <ul class="flex gap-4 max-md:gap-2 leading-[19px] max-md:flex-col">
            <li v-if="book.genres && book.genres.length > 0">
              <span class="text-[#8B8B8B]">Genre:</span> {{ book.genres[0].name }}
            </li>
            <li v-if="book.age_restriction">
              <span class="text-[#8B8B8B]">Age:</span> 18+
            </li>
            <li><span class="text-[#8B8B8B]">Status:</span> {{ book.is_completed ? 'Completed' : 'Ongoing' }}</li>
            <li><span class="text-[#8B8B8B]">Author:</span> {{ book.author }}</li>
            <li v-if="book.uploaded_by"><span class="text-[#5C5C5C]">Uploaded by {{ book.uploaded_by }}</span></li>
          </ul>
        </div>
      </div>
      <div class="max-md:col-span-2 flex flex-col gap-3 align-top h-full">
        <div
            v-html="book.description"
            class="flex-1 text-gray-600 dark:text-gray-400 line-clamp-2 italic max-md:hidden"
        />

        <div class="flex gap-2 w-full lg:max-w-md">
          <nuxt-link
              :to="`/books/${book.slug }`"
              class="lg:flex-1 py-2 max-md:py-2 px-4 max-md:px-3 border-[1px] text-lg border-primary text-center rounded-lg text-violet-800 dark:text-violet-500 dark:hover:text-violet-600"
          >
            Book page
          </nuxt-link>
          <a
              :href="getLinkToApp(book.id)"
              class="lg:flex-1 items-center py-2 max-md:py-2 px-4 max-md:px-4 text-lg bg-fm-gradient hover:bg-fm-gradient-active rounded-lg !text-white text-center"
          >
            Read book in the App
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>